import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useOrg } from '../../context/OrgContext';
import { useAIGeneration } from '../../context/AIGenerationContext';
import { useTour } from '../../context/TourContext';
import { useEmbedMode } from '../../context/EmbedContext';
import { useActiveVideoJobs } from '../../hooks/useActiveVideoJobs';
import { useEffectivePermissions } from '../../hooks/useEffectivePermissions';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import ElevanteLogoSVG from '../ElevanteLogoSVG';
import ElevanteIconSVG from '../ElevanteIconSVG';
import OrgSwitcher from '../OrgSwitcher';
import NotificationsDropdown from '../NotificationsDropdown';
import JobsWidget from '../JobsWidget';
import { AIAssistantPanel, AIAssistantButton } from '../AIAssistant';
import UserAvatar from '../UserAvatar';
import SetPasswordNudgeDialog from '../SetPasswordNudgeDialog';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';
import { toast } from 'sonner';
import api from '../../api/client';
import { SuperAdminPasswordChange } from '../SuperAdminPasswordChange';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { 
  Settings, 
  Users, 
  BookOpen, 
  BarChart3, 
  LogOut,
  ChevronRight,
  Menu,
  X,
  Shield,
  Sparkles,
  FlaskConical,
  HelpCircle,
  PanelRightOpen,
  LifeBuoy,
  Building2,
  Bot,
  Award,
  FileText,
  User,
  Pencil,
  TrendingUp,
  Trophy,
  Camera,
  Trash2,
  Upload,
  Compass,
  LayoutDashboard,
  ClipboardCheck,
  MessageSquare,
  Bell,
  Briefcase,
  Megaphone,
  Gauge,
  LayoutGrid
} from 'lucide-react';

const MainLayout = ({ children, rightPanel, hideHeader = false, flush = false }) => {
  const embed = useEmbedMode();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, organization, updateUser } = useAuth();
  const { orgSlug, buildPath, actualPath, currentOrg } = useOrg();
  const { safeNavigate: aiSafeNavigate, isGenerating } = useAIGeneration();
  const { startTour, tourCompleted } = useTour();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  // Auto-collapse the platform sidebar whenever the user lands on the
  // Course Builder OR the course preview page (viewed as instructor/admin)
  // so the page has full horizontal room for the topics + blocks columns
  // and the immersive learner-view-preview. Runs on every transition INTO
  // the route. Once on the page, manually re-expanding is honoured — the
  // effect won't fire again until the pathname changes.
  useEffect(() => {
    const isBuilder = /\/courses\/[^/]+\/edit(?:$|\/)/.test(location.pathname);
    // Course preview page: `/courses/:id` (no `/edit`). Auto-collapse only
    // for non-learner roles since instructors/admins typically reach this
    // via the builder's "Preview" button and want the same layout.
    const isCoursePreview =
      /^\/(?:[^/]+\/)?courses\/[^/]+\/?$/.test(location.pathname) &&
      user?.role && user.role !== 'Learner';
    if (isBuilder || isCoursePreview) {
      setSidebarCollapsed(true);
    }
  }, [location.pathname, user?.role]);

  const [aiAssistantOpen, setAiAssistantOpen] = useState(false);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [profileForm, setProfileForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [profileLoading, setProfileLoading] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const avatarInputRef = useRef(null);

  // Use organization from context or auth as fallback for branding
  const orgForBranding = currentOrg || organization;
  
  // Private label mode: show only org logo, hide Elevante branding
  const isPrivateLabel = orgForBranding?.isPrivateLabel && (orgForBranding?.logoUrl || orgForBranding?.logoFile);

  const isLearner = user?.role === 'Learner';
  const isInstructor = user?.role === 'Instructor' || user?.role === 'TenantAdmin' || user?.role === 'SuperAdmin';
  const isTenantAdmin = user?.role === 'TenantAdmin' || user?.role === 'SuperAdmin';

  const isSuperAdmin = user?.role === 'SuperAdmin';

  // 2026-08-06 — Granular RBAC gate for the sidebar. Previously items
  // like "AI Labs", "Industry Labs", "Analytics", "Background Jobs",
  // "Announcements" were shown to ANY instructor by role alone —
  // regardless of what permissions the tenant admin actually granted
  // them on their cohort assignments. Now we call `perms.can(<key>)`
  // which is true iff:
  //   * user is SuperAdmin/TenantAdmin (all-perms by role), OR
  //   * user is Instructor AND has that permission at partial/complete
  //     on at least one of their cohort assignments.
  // Instructors with zero grants for a given capability get that entry
  // hidden from the sidebar. See /api/instructors/me/effective-permissions.
  const perms = useEffectivePermissions();
  const isStrictInstructor = user?.role === 'Instructor';
  // For admins, every gate below is a no-op — the `can` helper always
  // returns true for them. Only strict-instructors hit the granular
  // checks. `hasAnyAssignment` gates the whole instructor-only
  // toolbelt (Dashboard etc.) so an instructor with 0 assignments
  // doesn't see Build/Manage/Personal at all.
  const canContentEdit = isTenantAdmin || perms.can('content.edit') || perms.can('content.publish');
  const canReview = isTenantAdmin || perms.can('submissions.review');
  const canAnalytics = isTenantAdmin || perms.can('analytics.view');
  const canCohortManage = isTenantAdmin || perms.can('cohort.manage');

  // Global "active Video pipeline jobs" indicator. Polls every 15s when
  // the user is instructor+ so a small badge can show on the Courses nav
  // item even while the user is on a different page (Analytics, Admin
  // Console, etc.). Auto-disables for Learners — they don't trigger video
  // generations, so the polling would always return zero.
  const { count: activeVideoJobsCount, jobs: activeVideoJobs } = useActiveVideoJobs({
    enabled: !!user && (user.role === 'Instructor' || user.role === 'TenantAdmin' || user.role === 'SuperAdmin'),
  });
  
  const openProfileDialog = () => {
    setProfileForm({
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      email: user?.email || '',
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setProfileDialogOpen(true);
  };
  
  const handleProfileSave = async () => {
    // Validate passwords if changing
    if (profileForm.newPassword) {
      if (profileForm.newPassword !== profileForm.confirmPassword) {
        toast.error('Error', { description: 'New passwords do not match' });
        return;
      }
      if (profileForm.newPassword.length < 6) {
        toast.error('Error', { description: 'Password must be at least 6 characters' });
        return;
      }
    }
    
    setProfileLoading(true);
    try {
      const updateData = {
        firstName: profileForm.firstName,
        lastName: profileForm.lastName
      };
      
      // Only include password if changing
      if (profileForm.newPassword) {
        updateData.password = profileForm.newPassword;
      }
      
      // Use the self-profile update endpoint (available to all users)
      await api.put('/auth/me', updateData);
      
      // Update local user state
      if (updateUser) {
        updateUser({
          ...user,
          firstName: profileForm.firstName,
          lastName: profileForm.lastName
        });
      }
      
      toast.success('Success', { description: 'Profile updated successfully' });
      setProfileDialogOpen(false);
    } catch (error) {
      toast.error('Error', { description: error.message || 'Failed to update profile' });
    } finally {
      setProfileLoading(false);
    }
  };

  // Handle avatar upload
  const handleAvatarUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Invalid file type', { description: 'Please upload a JPG, PNG, GIF, or WebP image.' });
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large', { description: 'Please upload an image smaller than 5MB.' });
      return;
    }

    setAvatarUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/auth/me/avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Update local user state with new avatar URL
      if (updateUser) {
        updateUser({
          ...user,
          avatarUrl: response.data.avatarUrl + '?t=' + Date.now() // Cache busting
        });
      }

      toast.success('Success', { description: 'Profile picture updated!' });
    } catch (error) {
      toast.error('Upload failed', { description: error.message || 'Failed to upload image.' });
    } finally {
      setAvatarUploading(false);
      // Reset input so same file can be selected again
      if (avatarInputRef.current) {
        avatarInputRef.current.value = '';
      }
    }
  };

  // Handle avatar deletion
  const handleAvatarDelete = async () => {
    if (!user?.avatarUrl) return;
    
    setAvatarUploading(true);
    try {
      await api.delete('/auth/me/avatar');

      // Update local user state to remove avatar
      if (updateUser) {
        updateUser({
          ...user,
          avatarUrl: null
        });
      }

      toast.success('Success', { description: 'Profile picture removed.' });
    } catch (error) {
      toast.error('Error', { description: 'Failed to remove profile picture.' });
    } finally {
      setAvatarUploading(false);
    }
  };

  const navItems = [
    // ─── PLATFORM (SuperAdmin only) ───
    ...(isSuperAdmin ? [
      { type: 'header', label: 'Platform', available: true, accent: '#DC2626' },
      { 
        icon: Building2, 
        label: 'Organizations', 
        path: '/superadmin', 
        color: 'text-[#DC2626]',
        available: true,
        group: 'platform'
      },
      {
        icon: Gauge,
        label: 'AI Diagnostic admin',
        path: '/superadmin/diagnostic',
        color: 'text-[#7C3AED]',
        available: true,
        group: 'platform'
      },
      {
        icon: Settings,
        label: 'Admin Console',
        path: '/admin',
        color: 'text-[#6366F1]',
        available: true,
        group: 'platform'
      },
      {
        icon: Megaphone,
        label: 'System Announcements',
        path: '/superadmin/announcements',
        color: 'text-[#DC2626]',
        available: true,
        group: 'platform'
      },
      {
        icon: Gauge,
        label: 'Performance',
        path: '/superadmin/perf',
        color: 'text-[#0EA5E9]',
        available: true,
        group: 'platform'
      },
    ] : []),

    // ─── LEARN / TEACH ───
    { type: 'header', label: isLearner ? 'Learn' : 'Teach', available: true, accent: '#F97316' },
    // Instructor's home — new dashboard with Pending reviews / Chats / Updates / Live cohorts tabs.
    {
      icon: LayoutDashboard,
      label: 'Dashboard',
      path: '/instructor/dashboard',
      color: 'text-[#6366F1]',
      // Strict-instructor only. `isInstructor` is role-inclusive (Instructor+),
      // which previously caused TenantAdmin/SuperAdmin to see the instructor
      // dashboard in their sidebar. Admins continue to land on /dashboard
      // (Courses) or /superadmin per their existing flows.
      available: user?.role === 'Instructor',
      group: 'core'
    },
    { 
      icon: BookOpen, 
      label: isLearner ? 'My Courses' : 'Courses', 
      path: '/courses', 
      color: 'text-[#F97316]',
      available: true,
      group: 'core'
    },
    // Industry Labs Library — learner entry (published labs only, enforced server-side).
    // Instructors/admins reach the same library via the Build section entry below.
    {
      icon: Compass,
      label: 'Industry Labs',
      path: '/industry-labs',
      color: 'text-[#EC4899]',
      available: isLearner,
      group: 'core'
    },
    // AI Diagnostic: open to every logged-in user (freemium; invite optional).
    {
      icon: Gauge,
      label: 'AI Diagnostic',
      path: '/diagnostic',
      color: 'text-[#7C3AED]',
      available: true,
      group: 'core'
    },
    // Learner-only Analytics — Instructor's Analytics moves into the Personal group below.
    { 
      icon: BarChart3, 
      label: 'Analytics', 
      path: '/analytics', 
      color: 'text-[#3B82F6]',
      available: false,
      group: 'core'
    },

    // ─── BUILD (Instructor+) — Now gated by content.edit / content.publish ───
    // Only visible if the user actually has permission to author content
    // on any of their cohorts. Strict-instructors with only submissions.review
    // + feedback.give see nothing in this section.
    ...((isInstructor && canContentEdit) ? [
      { type: 'header', label: 'Build', available: true, accent: '#8B5CF6' },
      // PRD: Course Merger / Gate U4 -- "Course Blueprint" sidebar entry removed.
      // The unified entry point is now the Courses page's "Create Course"
      // button which routes to /courses/new (CourseCreationWizard). The legacy
      // /content-builder route remains live as the AI engine, bridged via
      // ?bridgedCourseId=<id> from the wizard, until Gate U5 deletion.
      {
        icon: FlaskConical,
        label: 'AI Labs', 
        path: '/labs', 
        color: 'text-[#10B981]',
        available: true,
        group: 'build'
      },
      { 
        icon: Compass, 
        label: 'Industry Labs', 
        path: '/industry-labs', 
        color: 'text-[#EC4899]',
        available: true,
        group: 'build'
      },
    ] : []),

    // ─── MANAGE (TenantAdmin+ OR Instructor with cohort.manage) ───
    // 2026-08-06 — Was previously visible to ANY instructor by role.
    // Now gated by cohort.manage (or admin roles). Strict-instructors
    // without cohort.manage no longer see this section at all.
    ...((isTenantAdmin || isSuperAdmin || (isStrictInstructor && canCohortManage)) ? [
      { type: 'header', label: 'Manage', available: true, accent: '#F59E0B' },
      { 
        icon: Users, 
        label: 'Users & Cohorts', 
        path: '/users', 
        color: 'text-[#F59E0B]',
        available: true,
        group: 'admin'
      },
      // 2026-02-20 — Review tasks moved into MANAGE for TenantAdmin
      // (NOT SuperAdmin per explicit user request — SuperAdmins have
      // their own cross-org tooling). Sits right after Users & Cohorts
      // so tenant admins can jump straight from cohort roster → grading
      // inbox. Instructors continue to reach Review tasks via the
      // Personal section below (their existing muscle memory).
      ...((isTenantAdmin && !isSuperAdmin) ? [
        {
          icon: ClipboardCheck,
          label: 'Review tasks',
          path: '/instructor/inbox',
          color: 'text-[#F43F5E]',
          available: true,
          group: 'admin'
        },
        {
          icon: LayoutGrid,
          label: 'Review coverage',
          path: '/admin/review-coverage',
          color: 'text-[#D97706]',
          available: true,
          group: 'admin'
        },
        {
          icon: Megaphone,
          label: 'Announcements',
          path: '/admin/announcements',
          color: 'text-[#8B5CF6]',
          available: true,
          group: 'admin'
        },
      ] : []),
      ...(!isSuperAdmin && isTenantAdmin ? [
        { 
          icon: Building2, 
          label: 'Organization', 
          path: '/settings', 
          color: 'text-[#DC2626]',
          available: true,
          group: 'admin'
        },
      ] : []),
      ...((isTenantAdmin || isSuperAdmin) ? [
        {
          icon: BarChart3,
          label: 'Analytics',
          path: '/analytics',
          color: 'text-[#3B82F6]',
          available: true,
          group: 'admin'
        },
        { 
          icon: Trophy, 
          label: 'Gamification', 
          path: '/gamification-admin', 
          color: 'text-[#EAB308]',
          available: true,
          group: 'admin'
        },
        {
          icon: Briefcase,
          label: 'Background Jobs',
          path: '/jobs',
          color: 'text-[#F97316]',
          available: true,
          group: 'admin'
        },
      ] : []),
      ...((isTenantAdmin && !isSuperAdmin) ? [
        { 
          icon: Settings, 
          label: 'Admin Console', 
          path: '/admin', 
          color: 'text-[#6366F1]',
          available: true,
          group: 'admin'
        },
      ] : []),
    ] : []),

    // ─── PERSONAL (Learner & Instructor only — admins get Review tasks
    //     via the MANAGE section above per 2026-02-20 UX request.) ───
    ...((user?.role === 'Learner' || user?.role === 'Instructor') ? [
      { type: 'header', label: 'Personal', available: true, accent: '#10B981' },
      // Instructor-only: review queue + analytics + messaging surfaces all live in Personal.
      // 2026-08-06 — Each item now gated by its specific permission
      // instead of blanket `isInstructor`. Review tasks needs
      // submissions.review, Analytics needs analytics.view, Background
      // Jobs + Announcements are TenantAdmin-only surfaces so they're
      // hidden for strict-instructors entirely.
      ...(isInstructor ? [
        ...(canReview ? [{
          icon: ClipboardCheck,
          label: 'Review tasks',
          path: '/instructor/inbox',
          color: 'text-[#F43F5E]',
          available: true,
          group: 'personal'
        }] : []),
        ...(canAnalytics ? [{
          icon: BarChart3,
          label: 'Analytics',
          path: '/analytics',
          color: 'text-[#3B82F6]',
          available: true,
          group: 'personal'
        }] : []),
        // Background Jobs + Announcements are admin surfaces — hide
        // them from strict-instructors regardless of their grants.
        ...(!isStrictInstructor ? [
          {
            icon: Briefcase,
            label: 'Background Jobs',
            path: '/jobs',
            color: 'text-[#F97316]',
            available: true,
            group: 'personal'
          },
        ] : []),
        {
          icon: Bell,
          label: 'Notifications',
          path: '/notifications',
          color: 'text-[#A855F7]',
          available: true,
          group: 'personal'
        },
        ...(!isStrictInstructor ? [
          {
            icon: Megaphone,
            label: 'Announcements',
            path: '/admin/announcements',
            color: 'text-[#8B5CF6]',
            available: true,
            group: 'personal'
          },
        ] : []),
      ] : []),
      // Learner-only: original My Analytics + My Certificates entries.
      ...(isLearner ? [
        {
          icon: TrendingUp,
          label: 'My Analytics',
          path: '/my-analytics',
          color: 'text-[#10B981]',
          available: true,
          group: 'personal'
        },
        {
          icon: Award,
          label: 'My Certificates',
          path: '/my-certificates',
          color: 'text-[#F59E0B]',
          available: true,
          group: 'personal'
        },
      ] : []),
    ] : []),

    // ─── SUPPORT ───
    { type: 'header', label: 'Support', available: true, accent: '#6366F1' },
    { 
      icon: HelpCircle, 
      label: 'Help', 
      path: '/help', 
      color: 'text-[#6366F1]',
      available: true,
      group: 'support'
    },
    { 
      icon: Bot, 
      label: 'AI Assistant', 
      path: null,
      color: 'text-[#8B5CF6]',
      available: isInstructor || isTenantAdmin || isSuperAdmin,
      group: 'support',
      onClick: () => setAiAssistantOpen(true)
    },
    { 
      icon: FileText, 
      label: 'Documentation', 
      path: '/docs', 
      color: 'text-[#059669]',
      available: isSuperAdmin,
      group: 'support'
    },
  ];

  const handleNavigation = useCallback((path) => {
    // Use org-prefixed path if we have an org slug
    const targetPath = orgSlug ? buildPath(path) : path;
    // Use AI-safe navigation to check for ongoing generation
    aiSafeNavigate(navigate, targetPath);
  }, [orgSlug, buildPath, navigate, aiSafeNavigate]);

  const handleLogout = () => {
    // Check for ongoing AI generation before logout
    if (isGenerating) {
      const targetPath = orgSlug ? `/${orgSlug}/login` : '/login';
      aiSafeNavigate(navigate, targetPath);
      return;
    }
    logout();
    // Clear org context on logout
    localStorage.removeItem('currentOrgSlug');
    localStorage.removeItem('currentOrgId');
    const loginPath = orgSlug ? `/${orgSlug}/login` : '/login';
    navigate(loginPath);
  };

  // Check if current path matches, accounting for org prefix
  const isActive = (path) => {
    // Use actualPath (without org prefix) for comparison
    const currentPath = actualPath || location.pathname;
    return currentPath === path || currentPath.startsWith(path + '/');
  };
  // Most specific nav match wins (e.g. /superadmin/diagnostic over /superadmin).
  const bestActivePath = navItems
    .filter((item) => item.path && item.available !== false && isActive(item.path))
    .reduce((best, item) => (item.path.length > best.length ? item.path : best), '');

  // Embed mode: render only the children with no shell (no sidebar, no
  // header, no notifications, no org switcher, no AI assistant button).
  // Used by /embed/* routes that get loaded inside the Immersive Content
  // Player iframe so the lab/builder page fills the iframe cleanly.
  if (embed) {
    return (
      <div
        className="h-screen w-screen overflow-hidden bg-white"
        data-testid="embed-shell-main"
      >
        {children}
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      {/* Mobile Sidebar Overlay */}
      {mobileSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}

      {/* Left Sidebar Navigation */}
      <aside 
        className={`${
          sidebarCollapsed ? 'w-16 md:w-20' : 'w-56 md:w-60 lg:w-64'
        } bg-white border-r border-gray-200 flex flex-col transition-all duration-300 fixed lg:relative h-full z-50 ${
          mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
        data-testid="left-sidebar"
      >
        {/* Logo Section - h-16 to match main header - Dark Theme */}
        <div className="h-16 border-b border-slate-700 flex items-center px-3 md:px-4 bg-slate-900">
          {!sidebarCollapsed ? (
            <div className="flex items-center justify-between w-full gap-2">
              {/* Private Label Mode: Show only org logo */}
              {isPrivateLabel ? (
                <div className="flex items-center flex-1 min-w-0 justify-center">
                  <img 
                    src={orgForBranding.compactLogoFile || orgForBranding.compactLogoUrl || orgForBranding.logoFile || orgForBranding.logoUrl} 
                    alt={orgForBranding.name || 'Organization'} 
                    className="h-11 md:h-12 w-auto max-w-[180px] object-contain"
                    width="180"
                    height="48"
                  />
                </div>
              ) : (
                /* Co-branding: Elevante.AI logo on left (60%), Org logo on right (40%) */
                <div className="flex items-center flex-1 min-w-0 gap-2">
                  {/* Elevante.AI Logo - Gets more space (flex-[3]) */}
                  <div className="flex-[3] flex items-center justify-start min-w-0">
                    <ElevanteLogoSVG className="h-11 md:h-12 w-auto max-w-full" invertColors={true} />
                  </div>
                  
                  {/* Organization Compact Logo - Gets less space (flex-[2]) */}
                  {/* Uses compactLogoFile/compactLogoUrl (150x40px recommended) for sidebar, falls back to full logo */}
                  {(orgForBranding?.compactLogoFile || orgForBranding?.compactLogoUrl || orgForBranding?.logoFile || orgForBranding?.logoUrl) && (
                    <>
                      <div className="h-10 w-px bg-slate-600 flex-shrink-0" />
                      <div className="flex-[2] flex items-center justify-center min-w-0">
                        <img 
                          src={orgForBranding.compactLogoFile || orgForBranding.compactLogoUrl || orgForBranding.logoFile || orgForBranding.logoUrl} 
                          alt={orgForBranding.name || 'Organization'} 
                          className="h-10 md:h-11 w-auto max-w-[100px] md:max-w-[120px] object-contain"
                          width="120"
                          height="44"
                        />
                      </div>
                    </>
                  )}
                </div>
              )}
              {/* Desktop collapse button */}
              <button
                onClick={() => setSidebarCollapsed(true)}
                className="hidden lg:flex p-1 hover:bg-slate-700 rounded transition-colors flex-shrink-0 items-center justify-center"
                title="Collapse sidebar"
              >
                <X className="w-4 h-4 text-slate-400" />
              </button>
              {/* Mobile close button */}
              <button
                onClick={() => setMobileSidebarOpen(false)}
                className="lg:hidden flex p-1 hover:bg-slate-700 rounded transition-colors flex-shrink-0 items-center justify-center"
                title="Close sidebar"
              >
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => setSidebarCollapsed(false)}
              className="w-full flex items-center justify-center p-2 hover:bg-slate-700 rounded transition-colors"
              title="Expand sidebar"
            >
              {/* Collapsed state: Show Elevante icon */}
              <ElevanteIconSVG className="h-9 w-9 md:h-10 md:w-10" invertColors={true} />
            </button>
          )}
        </div>

        {/* Navigation Items - Dark Theme */}
        <nav className="flex-1 p-2 space-y-0.5 overflow-y-auto bg-slate-900">
          {navItems.map((item, index) => {
            if (!item.available) return null;
            
            // Section headers
            if (item.type === 'header') {
              if (sidebarCollapsed) {
                return (
                  <div key={`header-${item.label}`} className="my-2 mx-2">
                    <div className="h-px" style={{ background: `linear-gradient(to right, ${item.accent || '#475569'}, transparent)` }} />
                  </div>
                );
              }
              return (
                <div key={`header-${item.label}`} className={`flex items-center gap-2 px-3 ${index === 0 ? 'pt-1 pb-1.5' : 'pt-5 pb-1.5'}`}>
                  <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: item.accent || '#475569' }} />
                  <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 flex-shrink-0">{item.label}</span>
                  <div className="flex-1 h-px" style={{ background: `linear-gradient(to right, ${item.accent || '#475569'}88, transparent)` }} />
                </div>
              );
            }

            const Icon = item.icon;
            const active = item.path ? item.path === bestActivePath : false;
            
            return (
              <React.Fragment key={item.path || item.label}>
                <button
                  onClick={() => {
                    if (item.onClick) {
                      item.onClick();
                    } else if (item.path) {
                      handleNavigation(item.path);
                    }
                    setMobileSidebarOpen(false);
                  }}
                  className={`w-full flex items-center gap-2 md:gap-3 px-2 md:px-3 py-2.5 md:py-3 rounded-lg transition-all ${
                    active 
                      ? 'bg-slate-700 text-white shadow-sm' 
                      : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                  } ${sidebarCollapsed ? 'justify-center' : ''}`}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  title={sidebarCollapsed ? item.label : ''}
                >
                  <Icon className="w-4 h-4 md:w-5 md:h-5 flex-shrink-0" />
                  {/* Active Video pipeline jobs badge — appears next to
                      the "Courses" item when at least one Video generation
                      is running across any of the user's courses. */}
                  {item.path === '/courses' && activeVideoJobsCount > 0 && sidebarCollapsed && (
                    <span
                      className="relative flex h-2 w-2 -ml-1 self-start mt-0.5"
                      title={`${activeVideoJobsCount} video job${activeVideoJobsCount > 1 ? 's' : ''} in progress`}
                      data-testid="nav-courses-active-video-jobs-badge-dot"
                    >
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                    </span>
                  )}
                  {!sidebarCollapsed && <span className="font-medium text-xs md:text-sm truncate">{item.label}</span>}
                  {item.path === '/courses' && activeVideoJobsCount > 0 && !sidebarCollapsed && (
                    <span
                      className="ml-auto flex items-center gap-1"
                      title={
                        activeVideoJobs.length
                          ? `${activeVideoJobsCount} video job${activeVideoJobsCount > 1 ? 's' : ''} in progress:\n• ` +
                              activeVideoJobs.slice(0, 5).map(j => `${j.topic || 'Untitled'} (${j.status})`).join('\n• ') +
                              (activeVideoJobs.length > 5 ? `\n…and ${activeVideoJobs.length - 5} more` : '')
                          : `${activeVideoJobsCount} video job${activeVideoJobsCount > 1 ? 's' : ''} in progress`
                      }
                      data-testid="nav-courses-active-video-jobs-badge"
                    >
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                      </span>
                      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300">
                        {activeVideoJobsCount}
                      </span>
                    </span>
                  )}
                  {!sidebarCollapsed && active && !(item.path === '/courses' && activeVideoJobsCount > 0) && (
                    <ChevronRight className="w-3 h-3 md:w-4 md:h-4 ml-auto flex-shrink-0" />
                  )}
                </button>
              </React.Fragment>
            );
          })}
        </nav>

        {/* Powered by Elevante (Private Label Mode) - Dark Theme */}
        {isPrivateLabel && !sidebarCollapsed && (
          <div className="px-3 py-2 border-t border-slate-700 bg-slate-900">
            <div className="flex items-center justify-center gap-1.5 text-[10px] text-slate-500">
              <span>Powered by</span>
              <ElevanteLogoSVG className="h-4 w-auto opacity-60" invertColors={true} />
            </div>
          </div>
        )}

        {/* User Info at Bottom - Dark Theme */}
        <div className="p-2 md:p-3 border-t border-slate-700 bg-slate-800 flex-shrink-0">
          {!sidebarCollapsed ? (
            <div className="flex items-center justify-between gap-1 md:gap-2">
              <button 
                onClick={openProfileDialog}
                className="flex items-center gap-1 md:gap-2 min-w-0 flex-1 p-1 -m-1 rounded-lg hover:bg-slate-700 transition-colors group"
                title="Edit Profile"
              >
                <div className="relative flex-shrink-0">
                  <UserAvatar user={user} size="sm" />
                  <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 md:w-3.5 md:h-3.5 bg-slate-600 rounded-full flex items-center justify-center shadow-sm opacity-0 group-hover:opacity-100 transition-opacity">
                    <Pencil className="w-1.5 h-1.5 md:w-2 md:h-2 text-slate-300" />
                  </div>
                </div>
                <div className="flex-1 min-w-0 text-left">
                  <p className="text-[10px] md:text-xs font-semibold text-slate-200 truncate group-hover:text-white transition-colors" data-testid="user-name">
                    {user?.firstName} {user?.lastName}
                  </p>
                  <p className="text-[10px] md:text-xs text-slate-400 truncate">{user?.role}</p>
                </div>
              </button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="text-slate-400 hover:text-white hover:bg-slate-700 p-1.5 md:p-1 h-auto transition-colors flex-shrink-0"
                data-testid="btn-logout"
                title="Logout"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              <button
                onClick={openProfileDialog}
                className="w-full flex justify-center p-2 hover:bg-slate-700 rounded-lg transition-colors"
                title="Edit Profile"
              >
                <UserAvatar user={user} size="sm" />
              </button>
              <button
                onClick={handleLogout}
                className="w-full flex justify-center p-2 hover:bg-slate-700 rounded-lg transition-colors"
                data-testid="btn-logout-collapsed"
                title="Logout"
              >
                <LogOut className="w-5 h-5 text-slate-400" />
              </button>
            </div>
          )}
        </div>
      </aside>
      
      {/* Profile Edit Dialog */}
      <Dialog open={profileDialogOpen} onOpenChange={setProfileDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-orange-500" />
              Edit Profile
            </DialogTitle>
            <DialogDescription>
              Update your profile information. {user?.role !== 'SuperAdmin' && user?.role !== 'TenantAdmin' ? 'Some fields are managed by your administrator.' : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Profile Picture Upload Section */}
            <div className="flex flex-col items-center gap-3 pb-4 border-b">
              <div className="relative group">
                <UserAvatar user={user} size="xl" />
                <label 
                  className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity"
                  htmlFor="avatar-upload"
                >
                  {avatarUploading ? (
                    <div className="animate-spin rounded-full h-6 w-6 border-2 border-white border-t-transparent" />
                  ) : (
                    <Camera className="w-6 h-6 text-white" />
                  )}
                </label>
                <input
                  ref={avatarInputRef}
                  id="avatar-upload"
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  className="hidden"
                  onChange={handleAvatarUpload}
                  disabled={avatarUploading}
                />
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => avatarInputRef.current?.click()}
                  disabled={avatarUploading}
                >
                  <Upload className="w-4 h-4 mr-1" />
                  Upload Photo
                </Button>
                {user?.avatarUrl && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleAvatarDelete}
                    disabled={avatarUploading}
                    className="text-red-500 hover:text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Remove
                  </Button>
                )}
              </div>
              <p className="text-xs text-gray-500">JPG, PNG, GIF or WebP. Max 5MB.</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">First Name</Label>
                <Input
                  id="firstName"
                  value={profileForm.firstName}
                  onChange={(e) => setProfileForm({...profileForm, firstName: e.target.value})}
                  placeholder="First name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Last Name</Label>
                <Input
                  id="lastName"
                  value={profileForm.lastName}
                  onChange={(e) => setProfileForm({...profileForm, lastName: e.target.value})}
                  placeholder="Last name"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={profileForm.email}
                disabled
                className="bg-gray-50 text-gray-500"
              />
              <p className="text-xs text-gray-500">Email cannot be changed</p>
            </div>
            {user?.role === 'SuperAdmin' ? (
              <SuperAdminPasswordChange />
            ) : (
            <div className="border-t pt-4">
              <p className="text-sm font-medium mb-3">Change Password</p>
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="newPassword">New Password</Label>
                  <Input
                    id="newPassword"
                    type="password"
                    value={profileForm.newPassword}
                    onChange={(e) => setProfileForm({...profileForm, newPassword: e.target.value})}
                    placeholder="Leave blank to keep current"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm New Password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={profileForm.confirmPassword}
                    onChange={(e) => setProfileForm({...profileForm, confirmPassword: e.target.value})}
                    placeholder="Confirm new password"
                  />
                </div>
              </div>
            </div>
            )}
            <div className="bg-gray-50 rounded-lg p-3 text-sm">
              <p className="text-gray-600"><strong>Role:</strong> {user?.role}</p>
              <p className="text-gray-600"><strong>Organization:</strong> {orgForBranding?.name || 'N/A'}</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProfileDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleProfileSave} disabled={profileLoading}>
              {profileLoading ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden lg:ml-0">
        {/* Top Bar - Aligned across all panes (hidden when hideHeader is true) */}
        {!hideHeader && (
          <header className="h-14 md:h-16 bg-white border-b border-gray-200 flex items-center justify-between px-3 md:px-4 lg:px-6" data-testid="top-bar">
            <div className="flex items-center gap-2 md:gap-4 min-w-0">
              <button
                onClick={() => setMobileSidebarOpen(true)}
                className="lg:hidden p-2 hover:bg-orange-100 rounded-lg transition-colors flex-shrink-0"
              >
                <Menu className="w-5 h-5 text-[#4B5563]" />
              </button>
              <h2 className="text-base md:text-lg lg:text-xl font-bold text-[#4B5563] truncate" data-testid="page-header" style={{ fontFamily: "'Work Sans', sans-serif" }}>
                {navItems.find(item => item.path && item.path === bestActivePath)?.label || 
                 (location.pathname.includes('/labs/') && location.pathname.includes('/builder') ? 'Lab Builder' :
                  location.pathname.includes('/labs/') && location.pathname.includes('/results') ? 'Lab Results' :
                  location.pathname.includes('/labs/') && location.pathname.includes('/run') ? 'Run Lab' :
                  'Dashboard')}
              </h2>
            </div>
            
            <div className="flex items-center gap-1 md:gap-2 lg:gap-3 flex-shrink-0">
              {/* Product Tour Button */}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={startTour}
                      className="relative p-2 hover:bg-orange-50"
                      data-testid="product-tour-btn"
                    >
                      <Compass className="w-5 h-5 text-gray-600 hover:text-orange-600" />
                      {!tourCompleted && (
                        <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-orange-500 rounded-full animate-pulse" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    <p>{tourCompleted ? 'Restart Product Tour' : 'Start Product Tour'}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              
              {/* Background Jobs (renders nothing if user has no jobs) */}
              <JobsWidget />

              {/* Notifications */}
              <NotificationsDropdown />
              
              {/* Organization Switcher (SuperAdmin) or Organization Name */}
              {user?.role === 'SuperAdmin' ? (
                <OrgSwitcher />
              ) : organization && (
                <div className="hidden sm:flex items-center gap-2 px-2 md:px-4 py-1.5 md:py-2 bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg border border-orange-200">
                  <span className="text-xs md:text-sm font-semibold text-orange-800 truncate max-w-[100px] md:max-w-[150px] lg:max-w-none">{organization.name}</span>
                </div>
              )}
            </div>
          </header>
        )}

        {/* Middle + Right Panel Container */}
        <div className="flex-1 flex overflow-hidden">
          {/* Middle Content Pane - Always visible */}
          <main className={`flex-1 ${flush ? 'overflow-hidden' : 'overflow-y-auto'} ${(hideHeader || flush) ? 'p-0' : 'p-3 md:p-4 lg:p-6'} ${isLearner && !flush ? 'pb-20 md:pb-6' : ''}`} data-testid="main-content">
            {children}
          </main>

          {/* Right Detail Panel - Hidden on mobile, use Sheet instead */}
          {rightPanel && (
            <>
              {/* Mobile: Show toggle button and Sheet */}
              <div className="md:hidden fixed bottom-20 right-4 z-40">
                <Sheet>
                  <SheetTrigger asChild>
                    <Button 
                      size="sm" 
                      className="rounded-full shadow-lg bg-orange-500 hover:bg-orange-600 text-white"
                    >
                      <PanelRightOpen className="w-4 h-4 mr-1" />
                      Details
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="right" className="w-[85vw] sm:w-[350px] p-4 overflow-y-auto">
                    {rightPanel}
                  </SheetContent>
                </Sheet>
              </div>
              
              {/* Desktop: Show inline panel */}
              <aside className="hidden md:block w-72 lg:w-80 bg-white overflow-y-auto p-4 lg:p-5 border-l border-gray-200 flex-shrink-0" data-testid="right-panel">
                {rightPanel}
              </aside>
            </>
          )}
        </div>
        
        {/* Mobile Bottom Navigation for Learners */}
        {isLearner && (
          <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50 safe-area-bottom">
            <div className="flex justify-around items-center h-16">
              <button
                onClick={() => navigate(buildPath('/courses'))}
                className={`flex flex-col items-center justify-center flex-1 h-full px-2 ${
                  actualPath.includes('/courses') ? 'text-orange-600' : 'text-gray-500'
                }`}
              >
                <BookOpen className="w-5 h-5" />
                <span className="text-xs mt-1">{isInstructor ? 'Courses' : 'My Courses'}</span>
              </button>
              {isInstructor && canContentEdit && (
                <button
                  onClick={() => navigate(buildPath('/industry-labs'))}
                  className={`flex flex-col items-center justify-center flex-1 h-full px-2 ${
                    actualPath.includes('/industry-lab') ? 'text-orange-600' : 'text-gray-500'
                  }`}
                >
                  <Compass className="w-5 h-5" />
                  <span className="text-xs mt-1">Industry Labs</span>
                </button>
              )}
              <button
                onClick={() => navigate(buildPath('/my-certificates'))}
                className={`flex flex-col items-center justify-center flex-1 h-full px-2 ${
                  actualPath.includes('/my-certificates') ? 'text-orange-600' : 'text-gray-500'
                }`}
              >
                <Award className="w-5 h-5" />
                <span className="text-xs mt-1">Certificates</span>
              </button>
              <button
                onClick={() => navigate(buildPath('/help'))}
                className={`flex flex-col items-center justify-center flex-1 h-full px-2 ${
                  actualPath.includes('/help') ? 'text-orange-600' : 'text-gray-500'
                }`}
              >
                <HelpCircle className="w-5 h-5" />
                <span className="text-xs mt-1">Help</span>
              </button>
              {/* Profile/Logout Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="flex flex-col items-center justify-center flex-1 h-full px-2 text-gray-500"
                    data-testid="mobile-profile-menu"
                  >
                    <User className="w-5 h-5" />
                    <span className="text-xs mt-1">Profile</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 mb-2">
                  {/* User Info Header */}
                  <div className="px-3 py-2 border-b mb-1">
                    <p className="font-medium text-sm truncate">{user?.name || 'User'}</p>
                    <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                  </div>
                  <DropdownMenuItem onClick={openProfileDialog} className="cursor-pointer">
                    <Settings className="w-4 h-4 mr-2" />
                    Edit Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-600">
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </nav>
        )}
      </div>
      
      {/* AI Assistant Panel and Button */}
      <AIAssistantPanel 
        isOpen={aiAssistantOpen} 
        onClose={() => setAiAssistantOpen(false)}
        currentPage={location.pathname}
      />
      <AIAssistantButton 
        onClick={() => setAiAssistantOpen(!aiAssistantOpen)}
        isOpen={aiAssistantOpen}
      />

      {/* Post-OTP-login set-password nudge (EMAIL_OTP_AUTH_PRD §4.1).
          Renders only when the current user has mustSetPasswordOnFirstLogin
          in their profile — a single skippable modal on first post-migration
          login. Non-invasive: no-op for users who already have a password. */}
      <SetPasswordNudgeDialog />
    </div>
  );
};

export default MainLayout;
